import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import SectionHeader from "../components/common/SectionHeader";
import KPI from "../components/common/KPI";
import AuditableFigure from "../components/common/AuditableFigure";
import { useEngines, useClosures, useForecast, useTeam } from "../hooks/data";
import { getEngine } from "../config/engines";
import { fmtMoney, fmtNumber } from "../lib/format";
import { T } from "../lib/testids";

export default function Executive() {
  const { data: engines } = useEngines();
  const { data: closures } = useClosures(90);
  const { data: forecast } = useForecast();
  const { data: team } = useTeam();

  const closedTotal = (closures || []).reduce((s, c) => s + c.closedValue.amount, 0);
  const p50 = forecast?.points.find((p) => p.percentile === "p50");

  return (
    <div className="ambient-bg min-h-full">
      <div className="px-8 pt-8 pb-6 border-b border-[#17223a]">
        <SectionHeader
          overline="Panorama ejecutivo"
          title="Panel Ejecutivo"
          subtitle="Vista consolidada del negocio comercial: dónde está funcionando, dónde se está fugando dinero, dónde intervenir."
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-0 border-b border-[#17223a]">
        <div className="border-r border-[#17223a]">
          <KPI
            label="Dinero cerrado (90d)"
            value={fmtMoney(closedTotal)}
            deltaLabel={`${closures?.length || 0} cierres`}
            tone="success"
          />
        </div>
        <div className="border-r border-[#17223a]">
          <KPI
            label="Cuentas trabajadas"
            value={fmtNumber(
              (engines || []).reduce((s, e) => s + (e.actorsCount || 0), 0)
            )}
            deltaLabel="Suma motores activos"
          />
        </div>
        <div className="border-r border-[#17223a]">
          <KPI
            label="Proyección base (30d)"
            value={p50 ? fmtMoney(p50.value.amount) : "—"}
            deltaLabel="P50 · rango P10-P90"
            tone="amber"
          />
        </div>
        <div>
          <KPI
            label="Vendedores activos"
            value={fmtNumber(team?.length || 0)}
            deltaLabel="Con al menos 1 cierre"
          />
        </div>
      </div>

      <div className="p-8">
        <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-500 mb-4">
          Categorías de trabajo · vista ejecutiva
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {(engines || []).map((e) => {
            const cfg = getEngine(e.id);
            const isLive = e.status === "live";
            return (
              <div
                key={e.id}
                data-testid={T.executiveEngineBar(e.id)}
                className="border border-[#17223a] bg-[#0d1321] p-5"
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="text-sm font-display font-bold text-zinc-100">
                    {cfg.humanLabel}
                  </div>
                  <span
                    className={`text-[9px] px-1.5 py-0.5 font-mono tracking-[0.15em] ${
                      isLive
                        ? "bg-amber-500/15 text-amber-400"
                        : "border border-zinc-800 text-zinc-500"
                    }`}
                  >
                    {isLive ? "Activo" : "Próximamente"}
                  </span>
                </div>
                {isLive && e.potentialValue ? (
                  <AuditableFigure estimate={e.potentialValue} size="sm" tone="amber" label="Potencial abierto" />
                ) : (
                  <div className="text-xs text-zinc-600 font-mono">Sin activar</div>
                )}
                <div className="flex items-center justify-between mt-3 text-[10px] font-mono uppercase tracking-[0.15em] text-zinc-500">
                  <span>{fmtNumber(e.actorsCount)} cuentas</span>
                  <Link
                    to={cfg.path}
                    className="text-zinc-400 hover:text-amber-400 flex items-center gap-1"
                  >
                    Ver <ArrowUpRight size={11} />
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
